import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Collapse,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch, setSearchTerm, clearSearch } from '../store';

interface TopActionsProps {
  onSearch?: (query: string) => void;
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
}

const TopActions: React.FC<TopActionsProps> = ({
  onSearch,
  searchInputRef,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isCollapsed } = useAppSelector((state) => state.sidebar);
  const { searchTerm } = useAppSelector((state) => state.conversations);
  
  const dispatch = useAppDispatch();
  const [showSearch, setShowSearch] = useState(false);
  const [searchInput, setSearchInput] = useState(searchTerm);

  const handleSearchToggle = () => {
    setShowSearch(!showSearch);
    if (showSearch) {
      dispatch(clearSearch());
      setSearchInput('');
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    dispatch(setSearchTerm(value));
    onSearch?.(value);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    dispatch(clearSearch());
    onSearch?.('');
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSearch?.(searchInput);
  };

  // Collapsed state - show only search icon
  if (isCollapsed) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Tooltip title="Search conversations" placement="right">
          <IconButton
            onClick={handleSearchToggle}
            size="small"
            sx={{ width: 40, height: 40 }}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Search Section */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <form onSubmit={handleSearchSubmit} style={{ flex: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchInput}
          onChange={handleSearchChange}
          inputRef={searchInputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
            endAdornment: searchInput && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleSearchClear}
                  sx={{ p: 0.5 }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
            },
          }}
        />
      </form>
      </Box>
    </Box>
  );
};

export default TopActions; 